import React, { useRef, useEffect, useState, forwardRef } from 'react';
import { useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Player from '@vimeo/player';
import styles from '../styles/components/_VideoHeader.module.scss';
import { ReactComponent as PoneyclubLogoDesktop } from '../assets/images/poneyOverOutline.svg';
import { ReactComponent as PoneyclubLogoMobile } from '../assets/images/poneyOverOutlineMobile.svg';

const VideoHeader = forwardRef(({ overlayColor = 'var(--white)', applyDatesStroke }, ref) => {
    const location = useLocation();
    const iframeRef = useRef(null);
    const parallaxRef = useRef(null);
    const containerRef = useRef(null);
    const scrollDownRef = useRef(null);
    const [isHome, setIsHome] = useState(true);
    const [player, setPlayer] = useState(null);
    const [isVideoLoaded, setIsVideoLoaded] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        gsap.registerPlugin(ScrollTrigger);
        const mm = gsap.matchMedia();

        // Animation pour le scrollDown
        if (scrollDownRef.current) {
            gsap.to(scrollDownRef.current, {
                opacity: 0,
                y: -50,
                scrollTrigger: {
                    trigger: containerRef.current,
                    start: '20% top',
                    end: '40% top',
                    scrub: true,
                }
            });
        }

        // Parallax animation pour desktop
        mm.add('(min-width: 769px)', () => {
            ScrollTrigger.create({
                trigger: containerRef.current,
                start: 'top top',
                end: 'bottom top',
                scrub: true,
                onUpdate: (self) => {
                    gsap.set(parallaxRef.current, {
                        y: self.progress * 100,
                    });
                },
            });
        });

        // Parallax animation pour mobile
        mm.add('(max-width: 768px)', () => {
            ScrollTrigger.create({
                trigger: containerRef.current,
                start: 'top top',
                end: 'bottom top',
                scrub: true,
                onUpdate: (self) => {
                    gsap.set(parallaxRef.current, {
                        y: self.progress * 100,
                    });
                },
            });
        });

        return () => {
            ScrollTrigger.getAll().forEach((t) => t.kill());
            mm.revert();
        };
    }, [isVideoLoaded]); // Ajout de isVideoLoaded comme dépendance

    useEffect(() => {
        if (iframeRef.current) {
            const vimeoPlayer = new Player(iframeRef.current);
            setPlayer(vimeoPlayer);
            vimeoPlayer.on('loaded', () => {
                setIsVideoLoaded(true);
                if (location.pathname === '/') {
                    vimeoPlayer.play();
                }
            });
        }
        return () => {
            if (player) {
                player.off('loaded');
            }
        };
    }, []);

    useEffect(() => {
        const isHomePath = location.pathname === '/';
        setIsHome(isHomePath);
        if (player && isVideoLoaded) {
            if (isHomePath) {
                player.play();
            } else {
                player.pause();
            }
        }
    }, [location.pathname, player, isVideoLoaded]);

    return (
        <div
            ref={(el) => {
                containerRef.current = el;
                if (typeof ref === 'function') ref(el);
                else if (ref) ref.current = el;
            }}
            className={`${styles.bigHeader} ${!isHome ? styles.hidden : ''}`}
        >
            <div className={styles.videoWrapper}>
                <div ref={parallaxRef} className={styles.logoOver}>
                    {isMobile ? <PoneyclubLogoMobile /> : <PoneyclubLogoDesktop />}
                    <h2 className={`${styles.dates} ${applyDatesStroke ? styles['text--stroked'] : ''}`}>
                        Du 11 juin au 30 Aout 2025
                    </h2>
                </div>
                <div className={styles.videoContainer}>
                    <iframe
                        ref={iframeRef}
                        src="https://player.vimeo.com/video/1035563488?h=f49b4a97eb&background=1&autoplay=1&loop=1&byline=0&title=0&pip=false&airplay=false"
                        frameBorder="0"
                        allow="autoplay; fullscreen; picture-in-picture"
                        title="Video Header"
                    />
                </div>
                {isVideoLoaded && (
                    <div ref={scrollDownRef} className={styles.scrollDown}>
                        <div className={styles.scrollDown__icon}></div>
                    </div>
                )}
            </div>
        </div>
    );
});

export default VideoHeader;